import React, { createContext, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ButtonContext } from '../button/Button';
import Dialog from '../dialog/Dialog';
import Icon from '../icon/Icon';
import IconButton from '../icon_button/IconButton';
import Tag, { TagVariant } from '../tag/Tag';

const styles = require('./SectionEdition.module.scss');

const toDialogContext = (context: ButtonContext): 'talent' | 'company' | 'neutral' =>
  (context === 'semantic' ? 'neutral' : context);

interface SlotContextValue {
  buttonContext: ButtonContext;
  defaultDeleteConfirmation?: DeleteConfirmation;
  defaultLabel?: string;
}
const SlotContext = createContext<SlotContextValue>({ buttonContext: 'neutral' });

export interface SectionEditionTag {
  children: React.ReactNode;
  icon?: string;
  variant: TagVariant;
}

export type SectionEditionStatus = 'complete' | 'missing' | 'mandatory';
export type SectionAction = (() => void) | boolean;

const STATUS_TAGS: Record<Exclude<SectionEditionStatus, 'complete'>, SectionEditionTag> = {
  missing:   { variant: 'warning', icon: 'circleAlert',   children: 'Missing'   },
  mandatory: { variant: 'error',   icon: 'triangleAlert', children: 'Mandatory' },
};

export interface SectionEditionProps {
  allowMultiple?: boolean;
  children?: React.ReactNode;
  className?: string;
  context?: ButtonContext;
  defaultDeleteConfirmation?: DeleteConfirmation;
  iconName?: string;
  onAdd?: SectionAction;
  onEdit?: SectionAction;
  status?: SectionEditionStatus;
  tag?: SectionEditionTag;
  title: string;
  trailingText?: string;
}

const SectionEdition: React.FC<SectionEditionProps> = ({
  allowMultiple = false,
  children,
  className,
  context = 'neutral',
  defaultDeleteConfirmation,
  iconName,
  onAdd,
  onEdit,
  status,
  tag,
  title,
  trailingText,
}) => {
  const resolvedTag = tag ?? (status && status !== 'complete' ? STATUS_TAGS[status] : undefined);

  const slotContextValue = useMemo<SlotContextValue>(
    () => ({
      buttonContext: context,
      defaultDeleteConfirmation,
      defaultLabel: title.toLowerCase(),
    }),
    [context, defaultDeleteConfirmation, title],
  );

  return (
    <SlotContext.Provider value={slotContextValue}>
      <section className={classNames(styles.container, className)} aria-label={title}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            {iconName && (
              <div className={styles.iconWrapper}>
                <Icon name={iconName} color="neutral-500" size="md" aria-hidden="true" />
              </div>
            )}
            <h3 className={styles.title}>{title}</h3>
          </div>
          {resolvedTag && (
            <Tag variant={resolvedTag.variant} iconLeft={resolvedTag.icon}>
              {resolvedTag.children}
            </Tag>
          )}
          {onAdd && (allowMultiple || !children) && (
            <IconButton
              type="terceary"
              size="S"
              context={context}
              iconName="plus"
              onClick={typeof onAdd === 'function' ? onAdd : undefined}
              aria-label={`Add ${title.toLowerCase()}`}
            />
          )}
          {onEdit && !allowMultiple && children && (
            <IconButton
              type="terceary"
              size="S"
              context={context}
              iconName="pencil"
              onClick={typeof onEdit === 'function' ? onEdit : undefined}
              aria-label={`Edit ${title.toLowerCase()}`}
            />
          )}
          {trailingText && (
            <span className={styles.trailingText}>{trailingText}</span>
          )}
        </div>
        {children}
      </section>
    </SlotContext.Provider>
  );
};

export default SectionEdition;

export interface DeleteConfirmation {
  cancelText?: string;
  confirmText?: string;
  message?: React.ReactNode;
  title: string;
}

export interface SlotProps {
  children: React.ReactNode;
  context?: ButtonContext;
  deleteConfirmation?: DeleteConfirmation;
  label?: string;
  onDelete?: () => void;
  onEdit?: () => void;
}

export const Slot: React.FC<SlotProps> = ({
  children,
  context,
  deleteConfirmation,
  label,
  onDelete,
  onEdit,
}) => {
  const ctx = useContext(SlotContext);
  const resolvedContext = context ?? ctx.buttonContext;
  const resolvedLabel = label ?? ctx.defaultLabel;
  const resolvedDeleteConfirmation = deleteConfirmation ?? ctx.defaultDeleteConfirmation;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleDeleteClick = () => {
    if (!onDelete) return;
    if (resolvedDeleteConfirmation) {
      setIsConfirmOpen(true);
    } else {
      onDelete();
    }
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    onDelete?.();
  };

  return (
    <div className={styles.slot}>
      <div className={styles.slotContent}>{children}</div>
      <div className={styles.slotActions}>
        {onEdit && (
          <IconButton
            type="terceary"
            size="S"
            context={resolvedContext}
            iconName="pencil"
            onClick={onEdit}
            aria-label={resolvedLabel ? `Edit ${resolvedLabel}` : 'Edit'}
          />
        )}
        {onDelete && (
          <IconButton
            type="terceary"
            size="S"
            context={resolvedContext}
            iconName="trash"
            onClick={handleDeleteClick}
            aria-label={resolvedLabel ? `Delete ${resolvedLabel}` : 'Delete'}
          />
        )}
      </div>
      {resolvedDeleteConfirmation && (
        <Dialog
          isOpen={isConfirmOpen}
          title={resolvedDeleteConfirmation.title}
          message={resolvedDeleteConfirmation.message ?? ''}
          cancelText={resolvedDeleteConfirmation.cancelText ?? 'Cancel'}
          confirmText={resolvedDeleteConfirmation.confirmText ?? 'Delete'}
          context={toDialogContext(resolvedContext)}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
